// Live search results under the header search field.
// Delegation-based so parse order doesn't matter (see tk-catdrawer.js).
if (!customElements.get('tk-predictive')) {
  customElements.define(
    'tk-predictive',
    class extends HTMLElement {
      connectedCallback() {
        this.timer = null;
        this.controller = null;
        this.index = -1;

        this.minChars = parseInt(this.dataset.minChars, 10) || 3;
        this.delay = parseInt(this.dataset.delay, 10) || 250;

        this.addEventListener('input', (e) => {
          if (e.target.matches('[data-ps-input]')) this.queue(e.target.value);
        });

        this.addEventListener('keydown', (e) => this.onKey(e));

        this.addEventListener('focusin', () => {
          if (this.results && this.results.innerHTML.trim()) this.expand(true);
        });

        // Close when focus or a click lands outside the component.
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this.expand(false);
        });
      }

      get input() {
        return this.querySelector('[data-ps-input]');
      }

      get results() {
        return this.querySelector('[data-ps-results]');
      }

      queue(term) {
        clearTimeout(this.timer);
        const q = term.trim();

        if (q.length < this.minChars) {
          this.abort();
          this.expand(false);
          if (this.results) this.results.innerHTML = '';
          return;
        }

        this.timer = setTimeout(() => this.fetch(q), this.delay);
      }

      abort() {
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }
      }

      async fetch(q) {
        // Cancel any request still in flight so a slow early response
        // can't overwrite results for a newer query.
        this.abort();
        this.controller = new AbortController();

        const base = this.dataset.url || '/search/suggest';
        const params = new URLSearchParams({
          q,
          'resources[type]': this.dataset.types || 'product',
          'resources[limit]': this.dataset.limit || '10',
          // 'each' applies the limit per resource type. The API default is
          // 'all', which makes collections and suggestions eat into the
          // product count.
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': this.dataset.unavailable || 'last',
          section_id: 'tk-predictive-results',
        });

        if (this.dataset.fields) {
          params.set('resources[options][fields]', this.dataset.fields);
        }

        this.dataset.loading = 'true';

        try {
          const res = await fetch(`${base}?${params}`, { signal: this.controller.signal });
          if (!res.ok) throw new Error(res.statusText);

          const html = await res.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const inner = parsed.querySelector('.tk-ps__inner');

          if (this.results) {
            this.results.innerHTML = inner ? inner.outerHTML : '';
            this.index = -1;
            this.expand(Boolean(inner));
          }
        } catch (err) {
          if (err.name !== 'AbortError') this.expand(false);
        } finally {
          this.dataset.loading = 'false';
        }
      }

      expand(open) {
        this.dataset.open = String(open);
        if (this.input) this.input.setAttribute('aria-expanded', String(open));
      }

      get options() {
        return this.results ? Array.from(this.results.querySelectorAll('.tk-card__title a, .tk-ps__coll')) : [];
      }

      onKey(e) {
        if (e.key === 'Escape') {
          this.expand(false);
          if (this.input) this.input.focus();
          return;
        }

        const opts = this.options;
        if (!opts.length || this.dataset.open !== 'true') return;

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const step = e.key === 'ArrowDown' ? 1 : -1;
          this.index = (this.index + step + opts.length) % opts.length;
          opts.forEach((o, i) => o.classList.toggle('is-active', i === this.index));
          opts[this.index].scrollIntoView({ block: 'nearest' });
        }

        // Enter with nothing highlighted falls through to a normal
        // form submit, which lands on the full results page.
        if (e.key === 'Enter' && this.index > -1) {
          e.preventDefault();
          opts[this.index].click();
        }
      }
    }
  );
}